import i18next from "../../../i18n";
import { DeliveryTime, DeliveryRequest } from "../../../types/deliveries.type";
import { TO_BE_AGREED } from "../../../utils";
import { NewDeliveryFormValues } from "./newDeliveryValidationSchema";

export interface BuildDeliveryPayloadArgs {
  /** The validated RHF form values (`handleSubmit`'s `data`, or `getValues()` in the
   * multiple-supplier loop). */
  data: NewDeliveryFormValues;
  /** Current user from context — `user.id` and `user.company.id` are read exactly as the
   * pre-Carbon form read them. */
  user: { id?: string; company?: { id?: string } } | null | undefined;
  /** The uploaded purchase-order file URL from `useFileInputUploader` (or `""` when none). */
  uploadedSpecificationFileUrl: string | undefined;
  /** The selected delivery place id — the site id, the `TO_BE_AGREED` sentinel, or `""`. Comes
   * from RHF `siteId` (which the pre-Carbon form mirrored into a `deliveryPlaceId` state and read
   * back at submit); passing it in keeps the null-on-sentinel rule in one place. */
  deliveryPlaceId: string | null;
  /** The resolved order id (URL param / selected-supplier order / `""`). */
  selectedOrderId: string;
  /** The supplier this delivery is for (`""` when none resolved). */
  supplierId: string;
  /** The emails to notify. Differs per call path (single vs. per-supplier loop). */
  supplierEmails: string[];
}

/**
 * Builds the exact `DeliveryRequest` body the pre-Carbon `NewDeliveryForm` handed to
 * `useCreateDelivery` → `POST /deliveries`. Extracted during the Carbon migration (Task 6) so the
 * three former call sites (single manual, single selected-supplier, per-supplier loop) all produce
 * one provably byte-equivalent object — same keys, same value sources, same types, same
 * conditionals — instead of three hand-copied literals that could drift.
 *
 * The literal below is a verbatim reproduction of the pre-Carbon object, key for key:
 * `...data` first (so the FormValues keys keep their original order), then the same overrides in
 * the same order. `deliveryTime` is mapped through the same `i18next.t` morning/afternoon/night
 * cascade; `siteId` applies the same `TO_BE_AGREED ? null` rule; `receiverEmail` is the same
 * `trim() || undefined`.
 */
export const buildDeliveryPayload = ({
  data,
  user,
  uploadedSpecificationFileUrl,
  deliveryPlaceId,
  selectedOrderId,
  supplierId,
  supplierEmails,
}: BuildDeliveryPayloadArgs): DeliveryRequest => ({
  ...data,
  receiverEmail: data.receiverEmail?.trim() || undefined,
  user: { id: user?.id || "" },
  companyId: user?.company?.id || "",
  purchaseOrderFile: uploadedSpecificationFileUrl || "",
  siteId: deliveryPlaceId === TO_BE_AGREED ? null : deliveryPlaceId,
  orderId: selectedOrderId || "",
  supplierId,
  supplierEmails,
  deliveryTime:
    data.deliveryTime === DeliveryTime.MORNING
      ? (i18next.t("deliveries.form.morning") as DeliveryTime)
      : data.deliveryTime === DeliveryTime.AFTERNOON
        ? (i18next.t("deliveries.form.afternoon") as DeliveryTime)
        : (i18next.t("deliveries.form.night") as DeliveryTime),
});
